import { Outlet, useNavigate } from "react-router-dom";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Grid, IconButton, Stack, Typography } from "@mui/material";
import { TabSideNavigation, TabSideNavigationItem } from "@shared";

import { RoleChip } from "@/common/components/role-chip/RoleChip";
import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";

interface Props<TContext> {
  /** Heading of the detail page, typically the person's full name. */
  title: string;
  /** Role shown as a chip beside the title; omitted when the page shows roles elsewhere. */
  role?: UserPermission;
  /** Where the back button navigates to (the list this detail page was opened from). */
  backTo: string;
  backAriaLabel: string;
  /** Side navigation entries; `activeTabKey` matches their `key`. */
  navigationItems: TabSideNavigationItem[];
  activeTabKey?: string;
  /** Passed to the routed tab via `useOutletContext`. */
  context: TContext;
}

/**
 * Shared scaffold for the Systemeinstellungen person detail pages (Bearbeiter:in, Ehrenamtliche/r):
 * back button, title with an optional role chip, side navigation and the routed tab content. Kept in
 * one place so the two pages cannot drift apart; add a new detail page by supplying a title and the
 * navigation items rather than by copying this layout.
 */
export function DetailPageLayout<TContext>({
  title,
  role,
  backTo,
  backAriaLabel,
  navigationItems,
  activeTabKey,
  context,
}: Props<TContext>) {
  const navigate = useNavigate();

  return (
    <Stack flex={1} spacing={3} overflow={"hidden"}>
      <Stack direction="row" spacing={2} alignItems="center">
        <IconButton
          aria-label={backAriaLabel}
          onClick={() => void navigate(backTo)}
          sx={{ border: 1, borderColor: "divider" }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h1">{title}</Typography>
        {role && <RoleChip role={role} />}
      </Stack>

      <Grid container spacing={3} sx={{ flexGrow: 1, overflow: "hidden" }}>
        <Grid size={3} sx={{ display: "flex", flexDirection: "column", maxHeight: "100%", pb: 1 }}>
          <TabSideNavigation items={navigationItems} activeKey={activeTabKey} />
        </Grid>
        <Grid
          size={9}
          bgcolor={(theme) => theme.palette.fsw["blue-5"]}
          sx={{ display: "flex", flexDirection: "column", maxHeight: "100%", gap: 1, p: 2, overflow: "auto" }}
        >
          <Outlet context={context} />
        </Grid>
      </Grid>
    </Stack>
  );
}
